/**
 * LogicalEdge
 *
 * Renders the AND / OR connector lines between identity method nodes inside
 * the Identity Proof group. The label ("AND" or "OR") reflects the workflow's
 * verification_option setting:
 *   - "BOTH"   → AND  (user must complete all selected methods)
 *   - "EITHER" → OR   (user can complete any one of the selected methods)
 *
 * Where it appears in the flowchart:
 *   [GovtID] ──AND/OR──> [KBA] ──AND/OR──> [Bank]
 *                                  ↓ (AND)
 *                               [Selfie]
 *
 * The label pill is rendered via EdgeLabelRenderer which places it outside the
 * SVG layer so it can be styled with standard HTML/CSS (blue bordered pill).
 *
 * The path uses getSmoothStepPath with borderRadius: 0 for sharp right-angle
 * routing, consistent with the overall flowchart style.
 *
 * Props are passed automatically by ReactFlow when registered as an edge type.
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

function LogicalEdge({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    label,
}) {
    // Compute the SVG path and the midpoint coordinates for label placement
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 0, // sharp corners to match the flowchart's right-angle style
    });

    return (
        <>
            {/* The actual SVG line with optional arrow marker */}
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />

            {/* Label rendered in the HTML layer (outside SVG) so CSS styling applies fully */}
            {label && (
                <EdgeLabelRenderer>
                    <Box
                        style={{
                            position: 'absolute',
                            // Center the label pill exactly at the path midpoint
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'none',
                            zIndex: 10,
                        }}
                    >
                        <Typography component="span" className={styles.logicalEdgeLabel}>
                            {label}
                        </Typography>
                    </Box>
                </EdgeLabelRenderer>
            )}
        </>
    );
}

LogicalEdge.propTypes = {
    sourceX: PropTypes.number.isRequired,
    sourceY: PropTypes.number.isRequired,
    targetX: PropTypes.number.isRequired,
    targetY: PropTypes.number.isRequired,
    sourcePosition: PropTypes.string.isRequired,
    targetPosition: PropTypes.string.isRequired,
    style: PropTypes.object,
    markerEnd: PropTypes.string,
    label: PropTypes.string,
};

export default LogicalEdge;
